// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import {
    createElement,
    PropTypes,
    Hint,
    Tooltip,
    Translate,
    Grid,
    GridCol,
    Item,
} from '@plesk/ui-library';
import Link from '../../../../plesk/Link';
import SkinIcon from '../../../../plesk/SkinIcon';

const sectionsConfig = [
    {
        name: 'securityButtons',
        id: 'securityPanel',
        icon: '/icons/32/plesk/protect.png',
    },
    {
        name: 'troubleshootingButtons',
        id: 'troubleshootingPanel',
        icon: '/icons/32/plesk/support.png',
    },
    {
        name: 'serviceInstallationsButtons',
        id: 'servicesPanel',
        icon: '/icons/32/plesk/manage.png',
    },
    {
        name: 'toolsAndResourcesButtons',
        id: 'resourcesPanel',
        icon: '/icons/32/plesk/manage.png',
    },
    {
        name: 'generalButtons',
        id: 'generalPanel',
        icon: '/icons/32/plesk/preferences.png',
    },
    {
        name: 'serverManagementButtons',
        id: 'serverManagementPanel',
        icon: '/icons/32/plesk/server.png',
    },
    {
        name: 'statisticsButtons',
        id: 'statisticsPanel',
        icon: '/icons/32/plesk/statistics.png',
    },
    {
        name: 'mailButtons',
        id: 'mailPanel',
        icon: '/icons/32/plesk/mail.png',
    },
    {
        name: 'applicationsAndDatabasesButtons',
        id: 'applicationsAndDatabasesPanel',
        icon: '/icons/32/plesk/applications-and-databases.png',
    },
    {
        name: 'panelButtons',
        id: 'panelPanel',
        icon: '/icons/32/plesk/panel.png',
    },
    {
        name: 'uiButtons',
        id: 'uiPanel',
        icon: '/icons/32/plesk/branding.png',
    },
    {
        name: 'myPleskButtons',
        id: 'myPleskPanel',
        icon: '/icons/32/plesk/external-services.png',
    },
    {
        name: 'customButtons',
        id: 'customButtons',
        title: 'additionalServices',
        icon: '/icons/32/plesk/custom-buttons.png',
    },
];

const handlers = {
    rebootServer({ locale }) {
        Jsw.messageBox.show({
            type: Jsw.messageBox.TYPE_YESNO,
            subtype: 'reboot',
            text: locale.lmsg('buttonServerRebootConfirmationTitle'),
            description: String(locale.lmsg('buttonServerRebootConfirmationDescription')),
            onYesClick() {
                Jsw.redirectPost('/plesk/server/reboot/', { cmd: 'reboot' });
            },
            buttonTitles: {
                yes: locale.lmsg('components.buttons.yes'),
                no: locale.lmsg('components.buttons.no'),
            },
        });
    },
    shutdownServer({ locale }) {
        Jsw.messageBox.show({
            type: Jsw.messageBox.TYPE_YESNO,
            subtype: 'shutdown',
            text: locale.lmsg('buttonShutdownConfirmationTitle'),
            description: String(locale.lmsg('buttonShutdownConfirmationDescription')),
            onYesClick() {
                Jsw.redirectPost('/plesk/server/shutdown/', { cmd: 'shutdown' });
            },
            buttonTitles: {
                yes: locale.lmsg('components.buttons.yes'),
                no: locale.lmsg('components.buttons.no'),
            },
        });
    },
    'AdminPanel.ServerTools.showReporterPopup'({ props: { supportUrl, supportLocale } }) {
        new Jsw.ReporterPopup({
            supportUrl,
            cls: 'popup-panel',
            locale: supportLocale,
        });
    },
    showCookiePolicyPreferences() {
        window.requirejs(['app/cookie-policy'], function ({ togglePreferences }) {
            togglePreferences(true);
        });
    },
};

const cls = 'admin-server-tools';

const Tools = (props, { locale }) => {
    const { sections } = props;

    const handleClick = handle => {
        if (handlers[handle]) {
            return e => {
                e.preventDefault();
                handlers[handle]({ locale, props });
            };
        }

        return null;
    };
    const isTest = 'true' === Jsw.Cookie.get('debug-testing-mode');
    const maxColumnsNumber = ({ width }) => {
        if (width > 1800) {
            return 5;
        }

        if (width > 1100) {
            return 4;
        }

        if (width > 600) {
            return 3;
        }

        return 2;
    };

    return (
        <div data-id="tools-list">
            <Hint>
                <Translate content="pageHint" />
            </Hint>
            <Grid
                minColumnWidth={isTest ? 0 : 150}
                maxColumnsNumber={maxColumnsNumber}
                sm={isTest ? 3 : null}
                className={`${cls}__sections`}
                gap="md"
            >
                {sectionsConfig.filter(({ name }) => sections[name]).map(({ name, id, icon, title }) => (
                    <GridCol key={id}>
                        <Item
                            id={id}
                            icon={<SkinIcon src={icon} size="md" />}
                            title={
                                <span className={`${cls}__section-title`}>
                                    <Translate content={title || id} />
                                </span>
                            }
                        >
                            <ul
                                className={`${cls}__section-content`}
                                id={`${id}-tools-list`}
                                data-type="tools-section-content"
                            >
                                {sections[name].map(({ id, description, href, target, title, handler, attention }) => (
                                    <li key={id} id={id} className={`${cls}__section-item`}>
                                        <Tooltip title={description}>
                                            <Link
                                                to={href}
                                                target={target}
                                                onClick={handleClick(handler)}
                                            >
                                                {attention
                                                    ? <span className="hint-attention">{title}</span>
                                                    : title}
                                            </Link>
                                        </Tooltip>
                                    </li>
                                ))}
                            </ul>
                        </Item>
                    </GridCol>
                ))}
            </Grid>
        </div>
    );
};

Tools.propTypes = {
    sections: PropTypes.object.isRequired,
};

Tools.contextTypes = {
    locale: PropTypes.object,
};

export default Tools;



// WEBPACK FOOTER //
// ./views/admin/server/tools/Tools.js